import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../../contexts/user';
import { useOrganizationId } from '../../../helpers/auth-helpers/auth.hooks';
import { useGlobalStore } from '../../../store/global-store/global-store';
import {
  adoptFloraDraftApprovalMode,
  clearFloraDraftApprovalMode,
} from '../../../store/global-store/global-store.actions';
import type { AgentAttachmentsBinding } from '../components/chat/use-agent-attachments';
import { useAgentAttachments } from '../components/chat/use-agent-attachments';
import { isLatestTurnLive } from '../components/flora-feedback/flora-feedback.helpers';
import { extractArtifactsFromContent } from '../components/flora-message/flora-message.helpers';
import { createTurnId, deriveTurnsFromHistory } from '../components/flora-message/flora-turn-pairing.helpers';
import { useWebsocketHandlers } from '../flora-hooks';
import { stripContextPreamble } from '../flora-session-context-preamble';
import type { ContentBlock, FloraContextualInfo } from '../flora.type';
import { FloraNewChatReason, FloraVersion, type ChatHistory, type Message, type TokenUsage } from '../flora.type';

const ACTIVE_JOB_STORAGE_KEY = 'flora-active-job';

interface OutgoingMessageContext {
  /**
   * True until the conversation's first turn is sent — including when an
   * attachment upload has already minted the thread id, which must not make the
   * thread look like an existing chat.
   */
  isFreshThread: boolean;
}

interface UseFloraChatSessionOptions {
  /**
   * The externally selected thread: the URL's chat id on the full page, the
   * panel's own selection in the side panel. Undefined for a new chat.
   */
  chatId: string | undefined;
  /** Surface context sent with each run (the side panel's current objects). */
  contextualInfo?: FloraContextualInfo | null;
  /** Tells the backend the reply renders in the narrow side panel. */
  isSidebar: boolean;
  /**
   * Resume a job stashed in localStorage on mount (page refresh). The storage
   * key is global, so only one surface may opt in — two would both reconnect
   * the same job. Other surfaces still resume through the chat's `active_job_id`.
   */
  resumeStoredJob?: boolean;
  /**
   * Detach from the job when this surface unmounts: close the socket and clear
   * the shared reconnect key if it records this surface's job (the job keeps
   * running server-side and resumes through `active_job_id`). For side-panel
   * conversations, which end for good when they unmount — the right dock's on
   * leaving the app or changing organization, a page viewer's on leaving the
   * page. Other surfaces only close their socket on unmount, keeping the key so
   * their next mount resumes the job.
   */
  detachOnUnmount?: boolean;
  /**
   * Called after the session resets to a new chat, with why it reset (the full
   * page navigates to its base route, except on a context switch from a non-chat page).
   */
  onNewChat?: (reason: FloraNewChatReason) => void;
  /**
   * Called once a thread with no `chatId` has started its conversation, so the
   * host can select it (the full page catches the URL up). Must be stable.
   */
  onConversationStarted?: (threadId: string) => void;
  /** Rewrites a composer submission before it is sent (e.g. a first-turn context preamble). */
  prepareOutgoingMessage?: (message: string, context: OutgoingMessageContext) => string;
}

/**
 * Owns one Flora chat conversation — messages, streaming, history loading,
 * reconnecting to an in-flight or parked job, attachments and sending — for any
 * surface that renders Flora chat (full page and side panel). Route-specific
 * behaviour is injected through `options`, so every surface shares the same
 * race handling.
 *
 * Responsibilities:
 * 1. Hold the conversation state and wire it to `useWebsocketHandlers`
 * 2. Load a selected thread's history, and cache/restore a thread streamed this session
 * 3. Reconnect to the thread's live job (`active_job_id`, or the stored job when opted in)
 *    after its history renders, so a parked question card replays and can be answered
 * 4. Send turns with attachments, and stop a turn without orphaning its question
 *
 * @param {UseFloraChatSessionOptions} options - Surface-specific wiring
 * @returns The conversation state and actions for a chat surface
 */
function useFloraChatSession({
  chatId,
  contextualInfo = null,
  isSidebar,
  resumeStoredJob = false,
  detachOnUnmount = false,
  onNewChat,
  onConversationStarted,
  prepareOutgoingMessage,
}: UseFloraChatSessionOptions) {
  const orgId = useOrganizationId();
  const useAI = useGlobalStore((state) => state.useAI);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const floraModel = useGlobalStore((state) => state.floraModel);
  const floraEffort = useGlobalStore((state) => state.floraEffort);
  const { user } = useContext(UserContext);
  // Model/effort selection is super-user-only; only put it on the wire for
  // super-users so a stale persisted value can't leak from a downgraded user.
  const isSuperuser = Boolean(user?.is_superuser);

  const [messages, setMessages] = useState<Message[]>([]);
  const [questions, setQuestions] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState(chatId ?? '');
  const [chatHistories, setChatHistories] = useState<ChatHistory[]>([]);
  const [question, setQuestion] = useState('');
  const [isActiveQuestion, setIsActiveQuestion] = useState(false);
  const [isWaitingForFirstResponse, setIsWaitingForFirstResponse] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Flora is thinking...');
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);

  // Hosts pass these inline; read them through refs so a new closure each
  // render doesn't churn the callbacks and effects that use them.
  const onNewChatRef = useRef(onNewChat);
  onNewChatRef.current = onNewChat;
  const prepareOutgoingMessageRef = useRef(prepareOutgoingMessage);
  prepareOutgoingMessageRef.current = prepareOutgoingMessage;

  // Sync the selected chat into the working thread
  useEffect(() => {
    if (chatId) {
      setThreadId(chatId);
    }
  }, [chatId]);

  // Read by handleNewChat, so it doesn't change identity on every streamed chunk.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Ref to hold detachActiveJob so handleNewChat can call it without circular dependency
  const detachActiveJobRef = useRef<() => void>(() => {});

  const resetChat = useCallback(
    (reason: FloraNewChatReason) => {
      // New Chat abandons the view, not the work: detach, never cancel — the
      // job finishes (or waits on its card) server-side and is there on return.
      // Only the explicit Stop control cancels.
      // Mark a still-in-flight turn here, before the state below is cleared —
      // the thread-switch effect sees empty messages and can't. As there, a
      // turn parked on a question stays unmarked: the reconnect replays its card
      // on return, and a thinking indicator would claim Flora is still working.
      const latestMessages = messagesRef.current;
      const parkedOnQuestions = Boolean(latestMessages[latestMessages.length - 1]?.pendingQuestions?.length);
      if (threadId && (isActiveQuestion || isWaitingForFirstResponse) && !parkedOnQuestions) {
        liveWhenLeftRef.current.add(threadId);
      }
      detachActiveJobRef.current();
      setIsActiveQuestion(false);
      setIsWaitingForFirstResponse(false);
      setThreadId('');
      setQuestions([]);
      setMessages([]);
      setLoadingMessage('Flora is thinking...');
      setTokenUsage(null);
      // A fresh chat starts on the saved default, not a mode left on the landing.
      clearFloraDraftApprovalMode();
      onNewChatRef.current?.(reason);
    },
    [threadId, isActiveQuestion, isWaitingForFirstResponse],
  );

  const handleNewChat = useCallback(() => resetChat(FloraNewChatReason.User), [resetChat]);

  const activeVersion = FloraVersion.LANGCHAIN;

  // An upload on a brand-new chat mints the thread id before anything is sent.
  // Remember it so that thread still counts as fresh (see `isFreshThread`).
  // Carry an Ask/Auto choice made on the new chat onto that thread now: the
  // first turn's `job_created`/`is_update` adoption is skipped once a thread id
  // exists, so without this the toggle and the sent mode would fall back to the
  // saved default.
  const attachmentMintedThreadRef = useRef<string | null>(null);
  const setThreadIdFromAttachment = useCallback((id: string) => {
    attachmentMintedThreadRef.current = id;
    adoptFloraDraftApprovalMode(id);
    setThreadId(id);
  }, []);

  const attachmentsApi = useAgentAttachments({
    organizationId: orgId ?? '',
    threadId,
    setThreadId: setThreadIdFromAttachment,
  });

  const {
    runAgent,
    fetchChatHistories,
    handleDeleteHistory,
    handleEditHistory,
    handleAnswerSubmit,
    stopAgent,
    detachActiveJob,
    releaseActiveSocket,
    reconnectAgent,
    handleToolApproval,
  } = useWebsocketHandlers(
    orgId,
    useAI,
    threadId,
    activeVersion,
    portfolio?.id || null,
    contextualInfo,
    setThreadId,
    setChatHistories,
    setIsActiveQuestion,
    setIsWaitingForFirstResponse,
    setMessages,
    setLoadingMessage,
    handleNewChat,
    setTokenUsage,
    true, // enableArtifacts — the side panel doesn't render them; isSidebar discloses that
    isSuperuser ? floraModel : undefined,
    isSuperuser ? floraEffort : undefined,
    isSidebar,
    attachmentsApi.documentIds,
  );

  // Keep the ref in sync so handleNewChat can call it
  detachActiveJobRef.current = detachActiveJob;

  // Read once: whether this surface detaches on unmount never changes. A
  // surface that doesn't detach still closes its socket on unmount, keeping
  // the reconnect key so its next mount resumes the job.
  const detachOnUnmountRef = useRef(detachOnUnmount);
  const releaseActiveSocketRef = useRef(releaseActiveSocket);
  releaseActiveSocketRef.current = releaseActiveSocket;
  useEffect(() => {
    return () => {
      if (detachOnUnmountRef.current) {
        detachActiveJobRef.current();
      } else {
        releaseActiveSocketRef.current();
      }
    };
  }, []);

  // Clear active question state when switching threads so the UI is never locked
  // by a job running on a different thread. The background job keeps running —
  // results are persisted by the Celery task and will appear in chat history.
  // Also cache streamed messages from the departing thread so they can be restored.
  const prevChatIdRef = useRef(chatId);
  useEffect(() => {
    if (prevChatIdRef.current !== chatId) {
      const prevId = prevChatIdRef.current;
      prevChatIdRef.current = chatId;

      // Cache the departing thread's streamed content. Never cache empty
      // state (New Chat clears the arrays before this runs) — restoring it
      // would blank the thread and re-claim it in streamedThreadRef.
      if (prevId && streamedThreadRef.current === prevId && (messages.length > 0 || questions.length > 0)) {
        _messagesCacheRef.current.set(prevId, { messages, questions });
        // Mark mid-flight turns for the revisit's thinking indicator —
        // active_job_id can lag. A question interrupt stays unmarked: its
        // parked job is now exposed as active_job_id, so the reconnect effect
        // replays the card on revisit — the thinking flag would only double it.
        const lastMessage = messages[messages.length - 1];
        const parkedOnQuestions = Boolean(lastMessage?.pendingQuestions?.length);
        if ((isActiveQuestion || isWaitingForFirstResponse) && !parkedOnQuestions) {
          liveWhenLeftRef.current.add(prevId);
        }
      }
      optimisticThinkingRef.current = null;

      // Re-arm the reconnect dedupe — it only guards repeat attempts while
      // sitting on one thread, not across navigations.
      attemptedApprovalJobsRef.current.clear();

      // Skip clearing only when the selection is catching up to a thread we just
      // created: prevId is undefined (came from a new chat) AND chatId already
      // matches the in-flight threadId — we're still actively streaming on it.
      // In every other case (opening an existing chat from a new one, or moving
      // between real threads) we must clear so the UI is never locked.
      if (!(prevId === undefined && chatId === threadId)) {
        setIsActiveQuestion(false);
        setIsWaitingForFirstResponse(false);
      }
    }
  }, [chatId, threadId, messages, questions, isActiveQuestion, isWaitingForFirstResponse]);

  // Tell the host once a new thread's conversation has begun, so it can select
  // it. Wait for a real turn: attaching a document mints a thread id up front
  // (so the upload and the eventual message share it), but the user should stay
  // on the new-chat composer until they send.
  const hasStartedConversation = questions.length > 0 || messages.length > 0;
  useEffect(() => {
    if (threadId && !chatId && hasStartedConversation) {
      onConversationStarted?.(threadId);
    }
  }, [threadId, chatId, hasStartedConversation, onConversationStarted]);

  // Start a new chat when org or portfolio changes (skip initial mount). The
  // org resolving for the first time (null → id) is not a switch: nothing can
  // have been sent without an org, and resetting there would drop a chat the
  // host selected while it was still loading (e.g. the dock's refresh
  // restore). Any portfolio change once the org is known is a switch — a null
  // portfolio is a real state (an org with none), not just "not loaded yet".
  const prevContextRef = useRef({ orgId, portfolioId: portfolio?.id });
  useEffect(() => {
    const prev = prevContextRef.current;
    const portfolioId = portfolio?.id;
    if (prev.orgId === orgId && prev.portfolioId === portfolioId) return;
    prevContextRef.current = { orgId, portfolioId };
    const orgWasResolved = Boolean(prev.orgId);
    if (orgWasResolved) {
      resetChat(FloraNewChatReason.ContextSwitch);
    }
  }, [orgId, portfolio?.id, resetChat]);

  // Fetch chat histories on mount
  useEffect(() => {
    const cleanup = fetchChatHistories();
    if (cleanup) return cleanup;
  }, [fetchChatHistories]);

  // Refs for mount-only reconnection effect (avoids re-running when these change)
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  const reconnectAgentRef = useRef(reconnectAgent);
  reconnectAgentRef.current = reconnectAgent;
  const resumeStoredJobRef = useRef(resumeStoredJob);
  // The chat selected when this surface mounted, read by the mount-time resume.
  const initialChatIdRef = useRef(chatId);

  // In-flight job found in storage on mount (page refresh). The reconnect
  // is deferred until the thread's history renders — done immediately it
  // claims the thread, blocks the history load, and replays into a chat
  // with no question bubble.
  const storedActiveJobRef = useRef<{ jobId: string; threadId: string } | null>(null);
  // Stable across React StrictMode's double-invoke of this mount-only ([]) effect
  // — refs survive across an effect's repeated setup, so both invocations (and
  // the socket that ultimately wins ownership) agree on the same turnId.
  // Generating the id inside reconnectAgent itself would hand each invocation a
  // different id instead.
  const blindResumeTurnIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resumeStoredJobRef.current) return;
    try {
      const stored = localStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
      if (!stored) return;
      const { jobId, threadId: storedThreadId, orgId: storedOrgId } = JSON.parse(stored);
      if (!jobId || storedOrgId !== orgIdRef.current) return;

      if (storedThreadId) {
        // The URL's chat wins: a job stored for another thread (e.g. a
        // question parked on thread Y before the user opened chat X) must not
        // render Y under X's URL. That thread resumes via its active_job_id.
        if (initialChatIdRef.current && initialChatIdRef.current !== storedThreadId) return;
        setThreadId(storedThreadId);
        storedActiveJobRef.current = { jobId, threadId: storedThreadId };
        return;
      }
      // No thread recorded — there is no history to wait for, reconnect now.
      // There's no persisted question yet to derive a turnId from, so mint one.
      // The placeholder question is seeded from onReplayStarting (fired once
      // reconnect_status arrives), not here: with no threadId yet, seeding it
      // synchronously races the sibling history-load effect's own
      // `setQuestions([])` for a still-threadId-less chat in this same mount
      // commit — that effect runs after this one and always wins.
      if (!blindResumeTurnIdRef.current) {
        blindResumeTurnIdRef.current = createTurnId();
      }
      const blindResumeTurnId = blindResumeTurnIdRef.current;
      setIsActiveQuestion(true);
      setIsWaitingForFirstResponse(true);
      const cleanup = reconnectAgentRef.current(jobId, blindResumeTurnId, undefined, () => {
        setQuestions((prev) =>
          prev.length > 0
            ? prev
            : [{ content: '', role: 'user', timestamp: new Date().toISOString(), turnId: blindResumeTurnId }],
        );
      });
      if (cleanup) return cleanup;
    } catch {
      /* storage unavailable or invalid JSON */
    }
  }, []);

  // Refetch histories whenever a chat is opened — a stale active_job_id
  // (or stale null) means the thread never reconnects to its in-flight turn.
  useEffect(() => {
    if (chatId) {
      const cleanup = fetchChatHistories();
      if (cleanup) return cleanup;
    }
  }, [chatId, fetchChatHistories]);

  // Track threads where messages were set by streaming (not history load)
  const streamedThreadRef = useRef<string | null>(null);
  // Cache streamed messages when navigating away from a thread with active/recent streaming
  const _messagesCacheRef = useRef<Map<string, { messages: Message[]; questions: Message[] }>>(new Map());
  // Threads whose turn was still in flight when the user navigated away this
  // session. Fresher than active_job_id, which misses jobs that started after
  // the histories fetch; consumed on revisit to raise the thinking indicator.
  const liveWhenLeftRef = useRef<Set<string>>(new Set());
  // Thread showing an optimistic thinking indicator + the histories snapshot
  // it was based on. Exempts the raised flags from the reconnect effect's
  // "socket owns this thread" guard; a newer snapshot with no resumable job
  // clears the indicator (the turn finished while away).
  const optimisticThinkingRef = useRef<{ threadId: string; histories: ChatHistory[] } | null>(null);

  useEffect(() => {
    if ((isActiveQuestion || isWaitingForFirstResponse) && threadId) {
      // The optimistic indicator must never claim: the claim blocks both
      // history load and reconnect — a deadlocked spinner. The reconnect
      // claims for itself when it takes over.
      if (optimisticThinkingRef.current?.threadId !== threadId) {
        streamedThreadRef.current = threadId;
      }
    } else if (!isActiveQuestion && !isWaitingForFirstResponse && streamedThreadRef.current !== threadId) {
      // Thread has changed since streaming — allow history to load on revisit
      streamedThreadRef.current = null;
    }
  }, [isActiveQuestion, isWaitingForFirstResponse, threadId]);

  const latestTurnIsLive = isLatestTurnLive(
    isActiveQuestion,
    isWaitingForFirstResponse,
    threadId,
    streamedThreadRef.current,
    messages.length,
  );

  // Refetch chat histories when a new thread's conversation actually starts.
  // A pre-minted thread from a document attachment must not surface an empty
  // chat in the history list before the user sends, so wait for the first turn.
  // Always record the current threadId even before that turn: skipping the
  // update made opening an existing chat ('' → thread-a, then history populates
  // questions) look like a brand-new thread and open a second fetch socket
  // that stole reconnect.
  const prevThreadIdRef = useRef(threadId);
  const pendingNewThreadRefreshRef = useRef(false);
  useEffect(() => {
    const wasEmpty = !prevThreadIdRef.current;
    prevThreadIdRef.current = threadId;

    // New thread id on a new chat (attachment pre-mint or job_created before
    // the host selects it). Opening an existing chat already has chatId.
    if (wasEmpty && threadId && !chatId) {
      pendingNewThreadRefreshRef.current = true;
    }
    if (!threadId) {
      pendingNewThreadRefreshRef.current = false;
    }

    if (pendingNewThreadRefreshRef.current && hasStartedConversation) {
      pendingNewThreadRefreshRef.current = false;
      const cleanup = fetchChatHistories();
      if (cleanup) return cleanup;
    }
  }, [threadId, chatId, hasStartedConversation, fetchChatHistories]);

  // Load messages from chat history when threadId + histories are available
  useEffect(() => {
    // Don't overwrite messages that were set by streaming in this thread
    if (streamedThreadRef.current === threadId) return;

    const selectedHistory = threadId ? chatHistories.find((history) => history.id === threadId) : undefined;
    const liveJobId = selectedHistory?.active_job_id;
    const leftMidTurn = liveWhenLeftRef.current.has(threadId);

    // Restore the cached snapshot on revisit — unless the thread has (or
    // recently had) a resumable job: frames that arrived while away were
    // dropped, so the snapshot is frozen mid-turn (possibly missing the
    // approval card). Drop it and let the reconnect replay the full turn.
    const cached = _messagesCacheRef.current.get(threadId);
    if (cached) {
      if (liveJobId || leftMidTurn) {
        _messagesCacheRef.current.delete(threadId);
      } else {
        setMessages(cached.messages);
        setQuestions(cached.questions);
        _messagesCacheRef.current.delete(threadId);
        streamedThreadRef.current = threadId;
        return;
      }
    }

    // The in-flight turn is not in persisted history — raise the thinking
    // indicator now, or the render below paints a bare prompt until the
    // reconnect gets a fresh active_job_id.
    if (threadId && (liveJobId || leftMidTurn)) {
      liveWhenLeftRef.current.delete(threadId);
      optimisticThinkingRef.current = { threadId, histories: chatHistories };
      setIsActiveQuestion(true);
      setIsWaitingForFirstResponse(true);
    }

    if (threadId && chatHistories.length > 0) {
      if (selectedHistory) {
        // Pair questions/answers by a shared turnId (derived deterministically
        // from message order) rather than by array position — a turn with no
        // persisted assistant reply (e.g. it errored before one was written)
        // must not shift every later turn's answer onto the wrong question.
        const { questions: derivedQuestions, answers: derivedAnswers } = deriveTurnsFromHistory(
          selectedHistory.messages,
        );
        const lastAnswer = derivedAnswers[derivedAnswers.length - 1];

        const previousQuestions: Message[] = derivedQuestions.map((msg) => ({
          ...msg,
          content:
            typeof msg.content === 'string' &&
            msg.content.includes('Analyze the following data and provide a summary of the key findings.')
              ? 'Analyzing your data...'
              : typeof msg.content === 'string'
                ? stripContextPreamble(msg.content)
                : '',
        }));

        const previousAnswers: Message[] = derivedAnswers.map((msg) => ({
          ...msg,
          artifacts: msg.artifacts?.length ? msg.artifacts : extractArtifactsFromContent(msg.content),
          ...(msg === lastAnswer && selectedHistory.suggested_followups?.length
            ? { suggestedFollowups: selectedHistory.suggested_followups }
            : {}),
        }));

        // Inject context_compacted system messages into the assistant message
        // they followed. `deriveTurnsFromHistory` passes system messages
        // through untouched, so re-walk the raw history here, tracking how
        // many assistant messages have been seen so far to find the matching
        // (freshly-mapped) entry in previousAnswers by position.
        let assistantsSeen = 0;
        for (const msg of selectedHistory.messages) {
          if (msg.role === 'assistant') {
            assistantsSeen += 1;
            continue;
          }
          if (msg.role !== 'system' || typeof msg.content !== 'object' || Array.isArray(msg.content)) {
            continue;
          }
          const sysContent = msg.content as unknown as {
            type: string;
            context_used: number;
            context_limit: number;
            percentage: number;
          };
          if (sysContent.type === 'context_compacted' && assistantsSeen > 0) {
            const targetAnswer = previousAnswers[assistantsSeen - 1];
            const compactedBlock: ContentBlock = {
              type: 'context_compacted',
              context_used: sysContent.context_used,
              context_limit: sysContent.context_limit,
              percentage: sysContent.percentage,
            };
            // Append to existing content blocks, or wrap string content
            if (Array.isArray(targetAnswer.content)) {
              targetAnswer.content = [...targetAnswer.content, compactedBlock];
            } else {
              targetAnswer.content = [{ type: 'text' as const, text: targetAnswer.content as string }, compactedBlock];
            }
          }
        }
        setQuestions(previousQuestions);
        setMessages(previousAnswers);
        setTokenUsage(selectedHistory.token_usage ?? null);
      }
    } else if (!threadId) {
      streamedThreadRef.current = null;
      setQuestions([]);
      setMessages([]);
      setTokenUsage(null);
    }
  }, [threadId, chatHistories]);

  // Reconnect to the thread's live job once its history has rendered, so the
  // replayed turn pairs with the restored question bubble. Two sources, both
  // reload-survivors: the thread's active job (from the chat_histories payload
  // — works across devices and cleared storage) and the localStorage job
  // stashed by the mount effect above.
  const attemptedApprovalJobsRef = useRef<Set<string>>(new Set());
  const pendingApprovalCleanupRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => pendingApprovalCleanupRef.current?.(), []);
  useEffect(() => {
    if (!threadId) return;
    // An active question means a run (or another reconnect) already owns the
    // socket and message slot — the approval will arrive through it. The
    // optimistic thinking indicator is the exception: it raises these flags
    // purely for display, before any socket exists.
    const optimistic = optimisticThinkingRef.current?.threadId === threadId ? optimisticThinkingRef.current : null;
    if ((isActiveQuestion || isWaitingForFirstResponse) && !optimistic) return;
    // A thread this session already streamed stays owned by its live state:
    // chatHistories' active_job_id may be stale here (the turn may have just
    // finished), and a redundant reconnect would flash the whole turn UI.
    if (streamedThreadRef.current === threadId) return;
    if (chatHistories.length === 0) return;
    const selectedHistory = chatHistories.find((h) => h.id === threadId);
    const storedJob = storedActiveJobRef.current;
    const storedJobId = storedJob?.threadId === threadId ? storedJob.jobId : null;
    const resumableJobId = selectedHistory?.active_job_id ?? storedJobId;
    if (!resumableJobId) {
      // Nothing to resume: keep an optimistic indicator while its snapshot
      // is current (the post-hop refetch may still reveal the job); a newer
      // snapshot with no job means the turn finished — stop the spinner.
      if (optimistic && chatHistories !== optimistic.histories) {
        optimisticThinkingRef.current = null;
        setIsActiveQuestion(false);
        setIsWaitingForFirstResponse(false);
      }
      return;
    }
    if (attemptedApprovalJobsRef.current.has(resumableJobId)) return;
    attemptedApprovalJobsRef.current.add(resumableJobId);
    if (storedJobId) {
      storedActiveJobRef.current = null;
    }
    // Claim synchronously — the state updates below land a render later,
    // leaving a window where a histories refetch wipes the replayed turn.
    optimisticThinkingRef.current = null;
    streamedThreadRef.current = threadId;
    setIsActiveQuestion(true);
    setIsWaitingForFirstResponse(true);
    // Re-derive the trailing (still-unanswered) question's turnId straight from
    // the raw history rather than reading `questions` state — the sibling
    // history-load effect may not have committed its setQuestions yet in this
    // same render pass, so `questions` here could still be stale.
    const { questions: derivedQuestions, answers: derivedAnswers } = selectedHistory
      ? deriveTurnsFromHistory(selectedHistory.messages)
      : { questions: [], answers: [] };
    const trailingQuestion = derivedQuestions[derivedQuestions.length - 1];
    // A trailing question already has a persisted reply whenever `active_job_id`
    // (or the stashed job id) is stale relative to this particular history
    // snapshot — e.g. the snapshot was fetched before the new prompt reached
    // persisted history, so the *previous*, already-answered turn is still the
    // last entry. Reusing its turnId would let the renderers' turnId-keyed Map
    // overwrite that completed answer with the replay, while the actual
    // in-flight prompt gets no question bubble at all.
    const trailingQuestionAlreadyAnswered =
      trailingQuestion?.turnId !== undefined &&
      derivedAnswers.some((answer) => answer.turnId === trailingQuestion.turnId);
    let pendingTurnId = trailingQuestionAlreadyAnswered ? undefined : trailingQuestion?.turnId;
    if (!pendingTurnId) {
      // No persisted question to derive an id from yet — either the very first
      // turn of a brand-new chat reloaded before the user message was written
      // to history, or (per above) the trailing persisted question belongs to
      // an already-completed turn. Mint one and seed a placeholder question so
      // the replayed answer has something to pair with, mirroring the
      // mount-time blind-resume path above.
      pendingTurnId = createTurnId();
      // Append via a `prev`-based updater rather than rebuilding the array
      // from `derivedQuestions` directly: the sibling history-load effect's
      // own `setQuestions(...)` call (queued earlier in this same render
      // pass) applies its content normalization (context-preamble
      // stripping, the "Analyzing your data..." substitution) to
      // `derivedQuestions` before storing it — reusing the raw array here
      // would silently drop that normalization for the preserved,
      // already-answered trailing question.
      const placeholderQuestion: Message = {
        content: '',
        role: 'user',
        timestamp: new Date().toISOString(),
        turnId: pendingTurnId,
      };
      setQuestions((previousQuestions) => [...previousQuestions, placeholderQuestion]);
    }
    // Release the previous reconnect first: a job parked on a card sends no
    // frames, so its superseded socket would never close on its own.
    pendingApprovalCleanupRef.current?.();
    pendingApprovalCleanupRef.current = reconnectAgentRef.current(resumableJobId, pendingTurnId, () => {
      // Job finished before we attached (stale active_job_id): nothing was
      // replayed, so release the claim and re-trigger the history load with
      // a fresh array identity.
      streamedThreadRef.current = null;
      setChatHistories((previous) => previous.slice());
    });
  }, [threadId, chatHistories, isActiveQuestion, isWaitingForFirstResponse]);

  /**
   * Wraps stopAgent to ensure the pending question still gets a paired answer.
   * If stop is pressed before the agent created an assistant message
   * (streaming never started), inserts a placeholder stamped with the
   * pending question's turnId so it renders as that turn's (stopped) answer.
   */
  const handleStopAgent = useCallback(() => {
    // A placeholder answer is only worth adding when there is a question on
    // screen for it to attach to. `AgentMessageList` renders by walking
    // `questions` and looking each answer up by turnId, so an assistant message
    // whose turnId matches no question has no render path at all — it would be
    // stored and never shown. That is exactly the mount-time blind-resume case:
    // the paired question is seeded by `onReplayStarting`, which never fires
    // once Stop tears the socket down.
    if (isActiveQuestion && questions.length > 0) {
      // Prefer the trailing question's turnId, but fall back to the blind-resume
      // id: on a mount-time blind resume the paired placeholder question is only
      // seeded once `onReplayStarting` fires, so a stop before that would leave
      // `pendingTurnId` undefined and the stopped-answer placeholder unpaired
      // with the resumed turn.
      const pendingTurnId = questions[questions.length - 1]?.turnId ?? blindResumeTurnIdRef.current ?? undefined;
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        // If the last message already belongs to this turn (streaming had started), keep it.
        // Guard on pendingTurnId being defined so two turnId-less messages (e.g. both
        // missing one through some other gap) don't read as a false match.
        if (
          pendingTurnId !== undefined &&
          lastMessage &&
          lastMessage.role === 'assistant' &&
          lastMessage.turnId === pendingTurnId
        ) {
          return prevMessages;
        }
        // Streaming never started before stop — add a placeholder for this turn
        return [
          ...prevMessages,
          {
            content: '',
            role: 'assistant',
            timestamp: new Date().toISOString(),
            segments: [],
            turnId: pendingTurnId,
          },
        ];
      });
    }
    stopAgent();
  }, [stopAgent, isActiveQuestion, questions]);

  // Fresh until the first turn is sent. A thread id minted by an attachment
  // upload doesn't count as an existing chat, but any other id does — even
  // before its history has loaded.
  const isFreshThread = !hasStartedConversation && (!threadId || threadId === attachmentMintedThreadRef.current);

  /**
   * Sends one turn exactly as given, bypassing the composer: runs the agent,
   * adds the user bubble (with any ready attachments) and clears the sent
   * attachments. Does nothing while an attachment is still uploading, so it is
   * never sent half-done.
   *
   * @param {string} outgoingMessage - The wire message the agent receives
   * @param {string} displayContent - What the user bubble shows
   * @returns {(() => void) | undefined} The run's cleanup, when one was started
   */
  const sendTurn = useCallback(
    (outgoingMessage: string, displayContent: string) => {
      if (attachmentsApi.hasUploading) return;
      // The attached document ids ride along on the websocket payload (see
      // useWebsocketHandlers), and the backend appends the "read this doc" hint
      // and persists the refs.
      const readyAttachments = attachmentsApi.readyAttachments;
      const turnId = createTurnId();
      const cleanup = runAgent(outgoingMessage, turnId);
      const newMessage: Message = {
        content: displayContent,
        role: 'user',
        timestamp: new Date().toISOString(),
        turnId,
        ...(readyAttachments.length > 0 ? { attachments: readyAttachments } : {}),
      };
      setQuestions((prev) => [...prev, newMessage]);
      attachmentsApi.clearSentAttachments();
      return cleanup ?? undefined;
    },
    [runAgent, attachmentsApi],
  );

  /**
   * Sends the composer's message. `formattedMessage` is the expanded wire
   * message (mentions/slash commands); `displayMessage` is what the user typed,
   * shown in their bubble. Falls back to the current input when neither is given.
   *
   * @param {string} [formattedMessage] - The expanded message to send
   * @param {string} [displayMessage] - The text to show in the user bubble
   * @returns {(() => void) | undefined} The run's cleanup, when one was started
   */
  const submitQuestion = useCallback(
    (formattedMessage?: string, displayMessage?: string) => {
      if (attachmentsApi.hasUploading) return;
      const messageToSend = formattedMessage || question;
      if (!messageToSend) return;
      const outgoingMessage = prepareOutgoingMessageRef.current?.(messageToSend, { isFreshThread }) ?? messageToSend;
      const cleanup = sendTurn(outgoingMessage, displayMessage || messageToSend);
      setQuestion('');
      return cleanup;
    },
    [attachmentsApi.hasUploading, question, isFreshThread, sendTurn],
  );

  const handleDeleteAndReset = useCallback(
    (id: string) => {
      const cleanup = handleDeleteHistory(id);
      if (id === threadId) {
        handleNewChat();
      }
      return cleanup;
    },
    [handleDeleteHistory, threadId, handleNewChat],
  );

  const attachments = useMemo<AgentAttachmentsBinding>(
    () => ({
      items: attachmentsApi.attachments,
      addFiles: attachmentsApi.addFiles,
      removeAttachment: attachmentsApi.removeAttachment,
      hasUploading: attachmentsApi.hasUploading,
    }),
    [attachmentsApi.attachments, attachmentsApi.addFiles, attachmentsApi.removeAttachment, attachmentsApi.hasUploading],
  );

  return {
    messages,
    questions,
    threadId,
    chatHistories,
    question,
    setQuestion,
    isActiveQuestion,
    isWaitingForFirstResponse,
    loadingMessage,
    tokenUsage,
    latestTurnIsLive,
    isFreshThread,
    useAI,
    floraVersion: activeVersion,
    attachments,
    submitQuestion,
    sendTurn,
    handleNewChat,
    handleDeleteHistory: handleDeleteAndReset,
    handleEditHistory,
    handleAnswerSubmit,
    stopAgent: handleStopAgent,
    handleToolApproval,
    fetchChatHistories,
  };
}

export { useFloraChatSession };
export type { OutgoingMessageContext, UseFloraChatSessionOptions };
